import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTenderMeshStore } from "../store/tenderMeshStore";
import { useAIContext } from "../store/aiContextStore";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import { RED_FLAG_CODES } from "../../../engine/tenderRedFlags";

const FLAG_TKEY: Record<string, string> = {
  [RED_FLAG_CODES.SINGLE_BIDDER]: "tenderMesh.flagSingleBidder",
  [RED_FLAG_CODES.REPEAT_WINNER]: "tenderMesh.flagRepeatWinner",
  [RED_FLAG_CODES.ABNORMAL_VALUE]: "tenderMesh.flagAbnormalValue",
};

function defaultSince90d(): string {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString().split("T")[0]!;
}

function formatMoney(value: number | null, currency: string): string {
  if (value == null) return "n/a";
  const code = currency && currency.length === 3 ? currency : "EUR";
  try {
    return new Intl.NumberFormat("hu-HU", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("hu-HU")} ${code}`;
  }
}

/**
 * Global Tender Integrity Mesh (v2)
 */
export default function TenderMesh() {
  const { t } = useTranslation();
  const {
    vendorCount,
    conflictOfInterestFlags,
    transparencyScore,
    meshLatencyMS,
    procurementRiskScore,
    corruptionSignals,
    geopoliticalWeight,
    setVendorCount,
    setConflictOfInterestFlags,
    setTransparencyScore,
    setCorruptionSignals,
    setGeopoliticalWeight,
    simulateMeshLatency,
    exportTenderMesh,
    liveTenders,
    lastSyncAt,
    syncInProgress,
    redFlagsByPubNum,
    syncTenders,
    analyzeRedFlags,
  } = useTenderMeshStore();

  const conflictPenalty = useTenderMeshStore((s) => s.getConflictPenalty());
  const meshIntegrity = useTenderMeshStore((s) => s.getMeshIntegrity());
  const escalationLevel = useTenderMeshStore((s) => s.getEscalationLevel());

  const [error, setError] = useState<Error | null>(null);
  const [syncError, setSyncError] = useState<Error | null>(null);
  const [analyzeError, setAnalyzeError] = useState<Error | null>(null);
  const [country, setCountry] = useState("HU");
  const [cpv, setCpv] = useState("45");
  const [since, setSince] = useState(defaultSince90d);
  const [selectedPub, setSelectedPub] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  const selected = useMemo(
    () => liveTenders.find((n) => n.publicationNumber === selectedPub) || null,
    [liveTenders, selectedPub],
  );

  useEffect(() => {
    useAIContext.getState().setActivePanel("TenderMeshPanel");
  }, []);

  useEffect(() => {
    useAIContext.getState().setInput("vendorCount", vendorCount);
    useAIContext.getState().setInput("transparencyScore", transparencyScore);
    useAIContext.getState().setInput("conflictPenalty", conflictPenalty);
    useAIContext.getState().setInput("corruptionSignals", corruptionSignals);
    useAIContext.getState().setInput("geopoliticalWeight", geopoliticalWeight);
    useAIContext.getState().setInput("meshLatencyMS", meshLatencyMS);
    useAIContext.getState().setInput("procurementRiskScore", procurementRiskScore);
    useAIContext.getState().setInput("escalationLevel", escalationLevel);
  }, [vendorCount, transparencyScore, conflictPenalty, corruptionSignals, geopoliticalWeight, meshLatencyMS, procurementRiskScore, escalationLevel]);

  useEffect(() => {
    const newScore = useTenderMeshStore.getState().getProcurementRiskScore();
    if (useTenderMeshStore.getState().procurementRiskScore !== newScore) {
      useTenderMeshStore.setState({ procurementRiskScore: newScore });
    }
  }, [vendorCount, conflictOfInterestFlags, transparencyScore, corruptionSignals, geopoliticalWeight, meshLatencyMS]);

  const handleSync = async () => {
    setSyncError(null);
    setError(null);
    try {
      await syncTenders(country, cpv, since);
    } catch (e) {
      setSyncError(e instanceof Error ? e : new Error(t("tenderMesh.errorSync")));
    }
  };

  const handleAnalyze = async () => {
    if (!selectedPub) return;
    setAnalyzeError(null);
    setAnalyzing(true);
    try {
      await analyzeRedFlags(selectedPub);
    } catch (e) {
      setAnalyzeError(e instanceof Error ? e : new Error(t("tenderMesh.errorAnalyze")));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSimulate = () => {
    setError(null);
    try {
      simulateMeshLatency();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("tenderMesh.simulateFailed"));
      setError(err);
    }
  };

  const handleExport = () => {
    setError(null);
    try {
      exportTenderMesh();
    } catch (e) {
      const err = e instanceof Error ? e : new Error(t("tenderMesh.exportFailed"));
      setError(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="font-orbitron text-evat-neon text-xl mb-4">
        {t("tenderMesh.title")}
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} showRetry={!!error} />
      <ErrorBanner error={syncError} onRetry={() => setSyncError(null)} showRetry={!!syncError} />
      <ErrorBanner error={analyzeError} onRetry={() => setAnalyzeError(null)} showRetry={!!analyzeError} />

      {/* —— Live EU tenders (TED) —— */}
      <section className="border border-evat-gold/30 rounded-lg p-4 bg-black/40 space-y-4">
        <h2 className="font-orbitron text-lg text-evat-gold">{t("tenderMesh.liveTenders")}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("tenderMesh.country")}</label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            >
              <option value="HU">HU</option>
              <option value="DE">DE</option>
              <option value="AT">AT</option>
            </select>
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("tenderMesh.cpvCode")}</label>
            <input
              type="text"
              value={cpv}
              onChange={(e) => setCpv(e.target.value.replace(/[^\d]/g, ""))}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1">{t("tenderMesh.dateFrom")}</label>
            <input
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={handleSync}
              disabled={syncInProgress}
              className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded font-orbitron"
            >
              {syncInProgress ? t("tenderMesh.syncing") : t("tenderMesh.syncButton")}
            </button>
          </div>
        </div>

        {lastSyncAt && (
          <p className="text-xs text-neutral-500">
            {t("tenderMesh.lastSync")}: {new Date(lastSyncAt).toLocaleString()}
          </p>
        )}

        {syncInProgress && <LoadingSpinner text={t("tenderMesh.syncing")} />}

        {liveTenders.length === 0 && !syncInProgress ? (
          <p className="text-sm text-neutral-500">{t("tenderMesh.noTenders")}</p>
        ) : (
          <div className="overflow-x-auto max-h-80 overflow-y-auto border border-evat-gold/20 rounded">
            <table className="min-w-full text-sm text-left">
              <thead className="sticky top-0 bg-black/80 text-xs text-neutral-400 uppercase">
                <tr>
                  <th className="p-2">{t("tenderMesh.publicationNumber")}</th>
                  <th className="p-2">{t("tenderMesh.procedureSubject")}</th>
                  <th className="p-2">{t("tenderMesh.buyer")}</th>
                  <th className="p-2">{t("tenderMesh.estimatedValue")}</th>
                  <th className="p-2">{t("tenderMesh.awardedTo")}</th>
                  <th className="p-2">{t("tenderMesh.redFlags")}</th>
                </tr>
              </thead>
              <tbody>
                {liveTenders.map((n) => {
                  const ar = redFlagsByPubNum[n.publicationNumber];
                  return (
                    <tr
                      key={n.publicationNumber}
                      className={`border-t border-evat-gold/10 cursor-pointer ${
                        selectedPub === n.publicationNumber ? "bg-evat-gold/5" : ""
                      }`}
                      onClick={() => {
                        setSelectedPub(n.publicationNumber);
                        setAnalyzeError(null);
                      }}
                    >
                      <td className="p-2 font-mono text-xs">{n.publicationNumber}</td>
                      <td className="p-2 text-neutral-200 max-w-xs truncate">{n.title}</td>
                      <td className="p-2 text-neutral-300 max-w-[10rem] truncate">{n.buyerName}</td>
                      <td className="p-2">
                        {formatMoney(n.estimatedValue ?? n.awardedValue ?? null, n.currency)}
                      </td>
                      <td className="p-2 max-w-[10rem] truncate">
                        {n.awardedTo ||
                          (n.deadline && new Date(n.deadline).getTime() > Date.now()
                            ? "Tender lezárás után érhető el"
                            : t("tenderMesh.valueNotAvailable"))}
                      </td>
                      <td className="p-2">
                        {ar
                          ? ar.flags.length === 0
                            ? t("tenderMesh.valueNotAvailable")
                            : ar.flags.map((f) => (
                                <span
                                  key={f}
                                  className="inline-block mr-1 mb-1 px-2 py-0.5 rounded text-xs border border-amber-500/50 text-amber-200"
                                >
                                  {t(FLAG_TKEY[f] || "tenderMesh.redFlags")}
                                </span>
                              ))
                          : t("tenderMesh.valueNotAvailable")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selected && (
          <div className="border border-evat-gold/30 rounded p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-orbitron text-evat-gold text-sm">
                {t("tenderMesh.viewDetails")}: {selected.publicationNumber}
              </h3>
              <button
                type="button"
                onClick={handleAnalyze}
                disabled={analyzing}
                className="px-3 py-1.5 text-sm border border-evat-gold/60 text-evat-gold rounded"
              >
                {analyzing ? t("tenderMesh.syncing") : t("tenderMesh.analyzeRedFlags")}
              </button>
            </div>
            {analyzing && <LoadingSpinner text={t("tenderMesh.syncing")} />}
            {redFlagsByPubNum[selected.publicationNumber] && (
              <div className="text-sm text-neutral-300 space-y-1">
                <p>
                  {t("tenderMesh.riskSignalScore")}: {redFlagsByPubNum[selected.publicationNumber].score}
                </p>
                <p className="text-xs text-neutral-500 break-all">
                  {t("tenderMesh.fetchedAt")}: {lastSyncAt || t("tenderMesh.valueNotAvailable")}
                </p>
              </div>
            )}
          </div>
        )}
      </section>

      {/* —— Manual mesh simulation —— */}
      <section className="space-y-4">
        <h2 className="font-orbitron text-lg text-evat-neon/90">{t("tenderMesh.manualSimulation")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tenderMesh.vendorCount")}
          </label>
          <input
            type="number"
            min="0"
            value={vendorCount}
            onChange={(e) => setVendorCount(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tenderMesh.transparencyScore")}
          </label>
          <input
            type="number"
            min="0"
            max="100"
            value={transparencyScore}
            onChange={(e) => setTransparencyScore(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tenderMesh.corruptionSignals")}
          </label>
          <input
            type="number"
            min="0"
            value={corruptionSignals}
            onChange={(e) => setCorruptionSignals(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tenderMesh.conflictOfInterestFlags")}
          </label>
          <input
            type="number"
            min="0"
            value={conflictOfInterestFlags}
            onChange={(e) => setConflictOfInterestFlags(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-neutral-300">
            {t("tenderMesh.geopoliticalWeight")}
          </label>
          <input
            type="number"
            min="0"
            max="5"
            step="0.1"
            value={geopoliticalWeight}
            onChange={(e) => setGeopoliticalWeight(Number(e.target.value))}
            className="w-full bg-black border border-evat-gold/40 text-evat-neon rounded px-3 py-2 focus:outline-none focus:border-evat-gold"
          />
        </div>
      </div>

      <div className="flex gap-4">
        <button
          onClick={handleSimulate}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("tenderMesh.simulateLatency")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.transparencyScore")}
          </div>
          <div
            className={`text-lg font-orbitron ${
              transparencyScore >= 80
                ? "text-green-400"
                : transparencyScore >= 50
                  ? "text-yellow-400"
                  : "text-red-400"
            }`}
          >
            {transparencyScore}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.conflictPenalty")}
          </div>
          <div className="text-lg font-orbitron text-orange-400">
            {conflictPenalty.toFixed(1)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.corruptionSignals")}
          </div>
          <div className="text-lg font-orbitron text-red-400">
            {corruptionSignals}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.geopoliticalWeight")}
          </div>
          <div className="text-lg font-orbitron text-purple-400">
            {geopoliticalWeight.toFixed(1)}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.meshLatencyMS")}
          </div>
          <div className="text-lg font-orbitron text-evat-neon">
            {meshLatencyMS}ms
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.procurementRiskScore")}
          </div>
          <div
            className={`text-lg font-orbitron ${
              procurementRiskScore < 30
                ? "text-green-400"
                : procurementRiskScore < 60
                  ? "text-yellow-400"
                  : "text-red-400"
            }`}
          >
            {procurementRiskScore.toFixed(1)}%
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.escalationLevel")}
          </div>
          <div
            className={`text-lg font-orbitron ${
              escalationLevel === "high"
                ? "text-red-400"
                : escalationLevel === "medium"
                  ? "text-yellow-400"
                  : "text-green-400"
            }`}
          >
            {escalationLevel.toUpperCase()}
          </div>
        </div>

        <div className="border border-evat-gold/30 rounded p-3 bg-black/50">
          <div className="text-xs text-neutral-400 mb-1">
            {t("tenderMesh.meshIntegrity")}
          </div>
          <div
            className={`text-sm font-orbitron ${
              meshIntegrity >= 80
                ? "text-green-400"
                : meshIntegrity >= 50
                  ? "text-yellow-400"
                  : "text-red-400"
            }`}
          >
            {meshIntegrity.toFixed(1)}
          </div>
        </div>
      </div>
      </section>

      <div className="fixed bottom-4 right-4">
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-evat-gold/20 border border-evat-gold text-evat-gold rounded hover:bg-evat-gold/30 transition-colors font-orbitron disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t("tenderMesh.export")}
        >
          {t("tenderMesh.export")}
        </button>
      </div>
    </div>
  );
}
